import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../lib/supabaseClient';
import { API_ENDPOINTS } from '../config/constants';
import CardDetailModal from './CardDetailModal';
import CRMContactDetailModal from './CRMContactDetailModal';
import ListConfigModal from './ListConfigModal';
import CRMListConfigModal from './CRMListConfigModal';  // ✅ Modal específico para lista "Leads Recebidos"
import CRMAtendimentoConfigModal from './CRMAtendimentoConfigModal';  // ✅ Modal específico para lista "Em Atendimento"
import CRMAguardandoFormConfigModal from './CRMAguardandoFormConfigModal';  // ✅ Modal específico para lista "Aguardando Formulário"
import CRMFollowupListConfigModal from './CRMFollowupListConfigModal';  // ✅ Modal específico para listas "Follow-up 1/2/3"
import CRMGenericListConfigModal from './CRMGenericListConfigModal';
import CRMEquipeVendasConfigModal from './CRMEquipeVendasConfigModal';  // ✅ Modal específico para lista "Equipe de Vendas"
import MarketingCampaignEquipeVendasConfigModal from './MarketingCampaignEquipeVendasConfigModal';
import LoadingSpinner from './LoadingSpinner';
import EventModal from './EventModal';
import DayTimelineModal from './DayTimelineModal';
import CRMTimelineModal from './CRMTimelineModal';
import CRMAppointmentModal from './CRMAppointmentModal';
import CRMContactCard from './CRMContactCard';
import BarRestaurantOrderCard from './BarRestaurantOrderCard';
import BarRestaurantOrderDetailModal from './BarRestaurantOrderDetailModal';
import BarRestaurantTableCard, { BarRestaurantTableCardFromCard } from './BarRestaurantTableCard';
import BarRestaurantTableDetailModal from './BarRestaurantTableDetailModal';
import BarRestaurantTableKanban from './BarRestaurantTableKanban';
import BarRestaurantOrderKanban from './BarRestaurantOrderKanban';
import AddCardTypeModal from './AddCardTypeModal';
import SelectContactModal from './SelectContactModal';
import SearchContactModal from './SearchContactModal';
import EditCRMProjectModal from './EditCRMProjectModal';
import EditCRMProjectModalNew from './EditCRMProjectModalNew';
import EditCRMMultiProjectModal from './EditCRMMultiProjectModal';
import EditGasDistributorProjectModal from './EditGasDistributorProjectModal';
import EditMarketingCampaignProjectModal from './EditMarketingCampaignProjectModal';
import ServiceTaxModal from './ServiceTaxModal';
import GasDistributorNewClientCard from './GasDistributorNewClientCard';
import GasDistributorClientCard from './GasDistributorClientCard';
import GasDistributorClientModal from './GasDistributorClientModal';
import GasDistributorEntregaAgendadaConfigModal from './GasDistributorEntregaAgendadaConfigModal';
import GasDistributorEmEntregaConfigModal from './GasDistributorEmEntregaConfigModal';
import GasDistributorEntregueConfigModal from './GasDistributorEntregueConfigModal';
import GasDistributorEntregaImediataConfigModal from './GasDistributorEntregaImediataConfigModal';
import GasDistributorDistribuicaoFollowupConfigModal from './GasDistributorDistribuicaoFollowupConfigModal';
import GasDistributorClientesNovosConfigModal from './GasDistributorClientesNovosConfigModal';
import GasDistributorExtraListConfigModal from './GasDistributorExtraListConfigModal';
import GasDistributorNewManualClientModal from './GasDistributorNewManualClientModal';
import './KanbanBoard.css';
import AppIcon from './icons/AppIcon';

// ✅ CONFIGURAÇÃO MULTI-TENANT: Tipos de projeto e suas renderizações
const PROJECT_TYPES = {
  'kanban': {
    name: 'Kanban Padrão',
    headerButtons: ['kanban', 'calendar', 'edit'],
    viewModes: ['kanban', 'calendar'],
    defaultViewMode: 'kanban',
    hasLists: true,
    hasCards: true,
    cardTypes: ['normal']
  },
  'crm': {
    name: 'CRM',
    headerButtons: ['search_contact', 'kanban', 'calendar', 'edit'],
    viewModes: ['kanban', 'calendar'],
    defaultViewMode: 'kanban',
    hasLists: true,
    hasCards: true,
    cardTypes: ['contact', 'lead'],
    modals: ['CRMContactDetailModal', 'CRMListConfigModal']
  },
  'crm_multi': {
    name: 'CRM Multi Atendimento',
    headerButtons: ['search_contact', 'kanban', 'calendar', 'edit'],
    viewModes: ['kanban', 'calendar'],
    defaultViewMode: 'kanban',
    hasLists: true,
    hasCards: true,
    cardTypes: ['contact', 'lead'],
    modals: ['CRMContactDetailModal', 'CRMListConfigModal']
  },
  'bar_restaurant': {
    name: 'Bar & Restaurante',
    headerButtons: ['tables', 'orders', 'close_shift'],
    viewModes: ['tables', 'orders'],
    defaultViewMode: 'tables',
    hasLists: false,
    hasCards: false,
    cardTypes: ['table', 'order'],
    components: ['BarRestaurantTableKanban', 'BarRestaurantOrderKanban'],
    modals: ['BarRestaurantTableModal', 'BarRestaurantOrderModal']
  },
  'tasks': {
    name: 'Tarefas',
    headerButtons: ['kanban', 'calendar', 'edit'],
    viewModes: ['kanban', 'calendar'],
    defaultViewMode: 'kanban',
    hasLists: true,
    hasCards: true,
    cardTypes: ['task']
  },
  'custom': {
    name: 'Projeto Personalizado',
    headerButtons: ['kanban', 'calendar', 'edit'],
    viewModes: ['kanban', 'calendar'],
    defaultViewMode: 'kanban',
    hasLists: true,
    hasCards: true,
    cardTypes: ['normal']
  },
  'gas_distributor': {
    name: 'Distribuidora de Gás',
    headerButtons: ['new_client', 'kanban', 'calendar', 'edit'],
    viewModes: ['kanban', 'calendar'],
    defaultViewMode: 'kanban',
    hasLists: true,
    hasCards: true,
    cardTypes: ['order', 'gas_distributor_new_client', 'gas_distributor_client']
  },
  'marketing_campaign': {
    name: 'Campanha de marketing',
    headerButtons: ['search_contact', 'kanban', 'calendar', 'edit'],
    viewModes: ['kanban', 'calendar'],
    defaultViewMode: 'kanban',
    hasLists: true,
    hasCards: true,
    cardTypes: ['contact', 'lead'],
    modals: ['CRMContactDetailModal', 'CRMListConfigModal']
  }
};

/** CRM, CRM Multi e Campanha de marketing compartilham kanban/listas no mesmo estilo */
const isCrmLikeProjectType = (type) =>
  type === 'crm' || type === 'crm_multi' || type === 'marketing_campaign';

// ✅ Funções auxiliares para validação
const isValidProjectType = (type) => PROJECT_TYPES[type] !== undefined;
const isValidViewMode = (type, mode) => PROJECT_TYPES[type]?.viewModes?.includes(mode);
const isValidCardType = (type, cardType) => PROJECT_TYPES[type]?.cardTypes?.includes(cardType);

// Componente DroppableList para tornar a lista uma zona de drop
function DroppableList({ list, children, onScroll }) {
  const { setNodeRef } = useDroppable({
    id: list.id,
  });

  return (
    <div ref={setNodeRef} className="cards-container" onScroll={onScroll}>
      {children}
    </div>
  );
}

// Componente SortableCard para drag and drop
function SortableCard({ card, listId, onDelete, onOpenCard, projectType }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Se for projeto Gas Distributor e card de cliente novo, usar card personalizado
  if (projectType === 'gas_distributor' && card.entity_type === 'gas_distributor_new_client') {
    // Extrair número do WhatsApp do metadata
    const waChatId = card.metadata?.wa_chatid || '';
    const phoneNumber = waChatId.replace('@s.whatsapp.net', '').replace(/\D/g, '');
    
    // Formatar número de telefone
    const formatPhone = (phone) => {
      if (!phone || phone.length < 10) return phone;
      const ddd = phone.slice(0, 2);
      const parte1 = phone.slice(2, 7);
      const parte2 = phone.slice(7, 11);
      return `(${ddd}) ${parte1}-${parte2}`;
    };

    // Formatar data e hora
    const formatDateTime = (date) => {
      if (!date) return '';
      const d = new Date(date);
      const dateStr = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const timeStr = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      return `${dateStr} às ${timeStr}`;
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="kanban-card gas-distributor-new-client-card"
        onClick={() => onOpenCard(card, listId)}
      >
        <div className="gas-client-card-content">
          <div className="gas-client-name">
            <AppIcon name="person" />
            <h4>{card.title}</h4>
          </div>
          <div className="gas-client-phone">
            <AppIcon name="phone" />
            <span>{formatPhone(phoneNumber)}</span>
          </div>
          <div className="gas-client-time">
            <AppIcon name="schedule" />
            <span>{formatDateTime(card.created_at)}</span>
          </div>
        </div>
      </div>
    );
  }

  // Se for projeto CRM ou CRM Multi e card de contato, usar CRMContactCard
  if (isCrmLikeProjectType(projectType) && card.entity_type === 'contact') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
      >
        <CRMContactCard 
          card={card} 
          onOpenCard={(c) => onOpenCard(c, listId)}
          isDragging={isDragging}
        />
      </div>
    );
  }

  // Se for projeto Bar & Restaurante e card de pedido, usar BarRestaurantOrderCard
  if (projectType === 'bar_restaurant' && card.entity_type === 'bar_restaurant_order') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
      >
        <BarRestaurantOrderCard 
          card={card} 
          onOpenCard={(c) => onOpenCard(c, listId)}
          isDragging={isDragging}
        />
      </div>
    );
  }

  // Se for projeto Gas Distributor e card de cliente cadastrado, usar GasDistributorClientCard
  if (projectType === 'gas_distributor' && card.entity_type === 'gas_distributor_client') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
      >
        <GasDistributorClientCard 
          card={card} 
          onOpenCard={(c) => onOpenCard(c, listId)}
          isDragging={isDragging}
        />
      </div>
    );
  }

  // Detectar URLs na descrição
  const detectURLs = (text) => {
    if (!text) return [];
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.match(urlRegex) || [];
  };

  // Verificar se URL é imagem
  const getImagePreview = (url) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    return imageExtensions.some(ext => url.toLowerCase().includes(ext)) ? url : null;
  };

  const urls = detectURLs(card.description);
  const imageUrl = urls.find(url => getImagePreview(url));

  // Calcular status da data
  const getDueDateStatus = () => {
    if (!card.dueDate) return null;
    const now = new Date();
    const due = new Date(card.dueDate);
    const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'overdue';
    if (diffDays <= 1) return 'due-soon';
    return 'normal';
  };

  // Calcular progresso do checklist
  const getChecklistProgress = () => {
    if (!card.checklist || card.checklist.length === 0) return null;
    const completed = card.checklist.filter(item => item.completed).length;
    const total = card.checklist.length;
    return { completed, total, percentage: Math.round((completed / total) * 100) };
  };

  const handleCardClick = (e) => {
    // Se clicou no handle de drag, não abre modal
    if (e.target.closest('.card-drag-handle')) {
      return;
    }
    onOpenCard(card, listId);
  };

  const dueDateStatus = getDueDateStatus();
  const checklistProgress = getChecklistProgress();

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="kanban-card"
      onClick={handleCardClick}
    >
      {/* Handle para arrastar */}
      <div className="card-drag-handle">
        <AppIcon name="drag_indicator" />
      </div>

      {/* Capa do card (imagem ou cor) */}
      {imageUrl && (
        <div className="card-cover">
          <img src={imageUrl} alt="Cover" />
        </div>
      )}

      {/* Conteúdo do card */}
      <div className="card-content">
        {/* Labels */}
        {card.labels && card.labels.length > 0 && (
          <div className="card-labels">
            {card.labels.slice(0, 3).map((label, idx) => (
              <span
                key={idx}
                className="card-label"
                style={{ backgroundColor: label.color }}
              >
                {label.name}
              </span>
            ))}
            {card.labels.length > 3 && (
              <span className="card-label" style={{ background: '#dfe1e6', color: '#172b4d' }}>
                +{card.labels.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Título */}
        <h4>{card.title}</h4>

        {/* Badges (metadados visuais) */}
        <div className="card-badges">
          {/* Data de vencimento */}
          {card.dueDate && (
            <div className={`card-badge ${dueDateStatus === 'overdue' ? 'overdue' : dueDateStatus === 'due-soon' ? 'due-soon' : ''}`}>
              <AppIcon name="schedule" />
              {new Date(card.dueDate).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
            </div>
          )}

          {/* Checklist */}
          {checklistProgress && (
            <div className={`card-badge ${checklistProgress.percentage === 100 ? 'completed' : ''}`}>
              <AppIcon name="checklist" />
              {checklistProgress.completed}/{checklistProgress.total}
            </div>
          )}

          {/* Comentários */}
          {card.comments && card.comments.length > 0 && (
            <div className="card-badge">
              <AppIcon name="chat_bubble" />
              {card.comments.length}
            </div>
          )}

          {/* Anexos */}
          {card.attachments && card.attachments.length > 0 && (
            <div className="card-badge">
              <AppIcon name="attach_file" />
              {card.attachments.length}
            </div>
          )}

          {/* Links */}
          {card.links && card.links.length > 0 && (
            <div className="card-badge">
              <AppIcon name="link" />
              {card.links.length}
            </div>
          )}

          {/* Descrição */}
          {card.description && (
            <div className="card-badge">
              <AppIcon name="subject" />
            </div>
          )}
        </div>
      </div>

      <button
        className="btn-delete-card"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(listId, card.id);
        }}
      >
        <AppIcon name="delete" />
      </button>
    </div>
  );
}

// ✅ Componente SortableList para tornar listas arrastáveis
// listeners separados: apenas o header (via dragHandleProps) ativa o drag da lista
function SortableList({ list, children, isDragging, isMinimized, dragHandleRef }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: list.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`kanban-list ${isMinimized ? 'minimized' : ''}`}
    >
      {typeof children === 'function' ? children({ dragHandleProps: { ...listeners, ...attributes } }) : children}
    </div>
  );
}

// Componente principal KanbanBoard
export default function KanbanBoard({ projectId, onBack }) {
  const [project, setProject] = useState(null);
  const [lists, setLists] = useState([]);
  const [listPagination, setListPagination] = useState({});
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedCardListId, setSelectedCardListId] = useState(null);
  const [showListConfigModal, setShowListConfigModal] = useState(false);
  const [editingListData, setEditingListData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [listMenuOpen, setListMenuOpen] = useState(null);
  const [showAddCardTypeModal, setShowAddCardTypeModal] = useState(false);
  const [showSelectContactModal, setShowSelectContactModal] = useState(false);
  const [showSearchContactModal, setShowSearchContactModal] = useState(false);
  const [selectedListForCard, setSelectedListForCard] = useState(null);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [activeList, setActiveList] = useState(null);  // ✅ Rastrear lista ativa durante drag
  const [editingList, setEditingList] = useState(null);
  const [minimizedLists, setMinimizedLists] = useState({});  // ✅ Rastrear listas minimizadas
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'calendar'
  
  // Estados do Calendário
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDayTimeline, setShowDayTimeline] = useState(false);

  // Estado para modal de pedido Bar & Restaurante
  const [showBarRestaurantOrderModal, setShowBarRestaurantOrderModal] = useState(false);
  const [selectedBarRestaurantOrder, setSelectedBarRestaurantOrder] = useState(null);

  // Estado para modal de mesa Bar & Restaurante
  const [showBarRestaurantTableModal, setShowBarRestaurantTableModal] = useState(false);
  const [selectedBarRestaurantTable, setSelectedBarRestaurantTable] = useState(null);
  const [barRestaurantTables, setBarRestaurantTables] = useState([]);

  const [showCloseShiftModal, setShowCloseShiftModal] = useState(false);
  const [closingShift, setClosingShift] = useState(false);

  // Estado para modal de taxa de serviço
  const [showServiceTaxModal, setShowServiceTaxModal] = useState(false);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [showNewManualClientModal, setShowNewManualClientModal] = useState(false);

  // Configurar sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Precisa mover 8px para ativar o drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Carregar projeto do Supabase
  useEffect(() => {
    if (projectId) {
      loadProject();
      loadLists();
    }
  }, [projectId]);

  const CARDS_PAGE_SIZE = 10;

  const getCardsSelect = () => {
    // ✅ Selecionar campos essenciais para renderização
    // Campos pesados (checklist, comments, attachments, links, custom_fields) 
    // são carregados apenas quando abrir o modal do card
    // metadata é necessário para cards de gas_distributor_new_client (wa_chatid)
    return [
      'id',
      'title',
      'description',
      'display_order',
      'due_date',
      'priority',
      'entity_type',
      'entity_id',
      'assigned_to',
      'created_at',
      'list_id',
      'metadata'
    ].join(',');
  };

  // ✅ Carregar campos pesados apenas quando abrir o modal do card
  const fetchCardFullDetails = async (cardId) => {
    const user = JSON.parse(localStorage.getItem('astro_user_data'));
    if (!user?.company_id) return null;

    const { data, error } = await supabase
      .from('project_items')
      .select('checklist, comments, attachments, links, metadata, custom_fields')
      .eq('id', cardId)
      .eq('company_id', user.company_id)
      .single();

    if (error) {
      console.error('❌ Erro ao carregar detalhes do card:', error);
      return null;
    }

    return data;
  };

  const fetchCardsPageForList = async ({ listId, offset }) => {
    const user = JSON.parse(localStorage.getItem('astro_user_data'));
    if (!user?.company_id) return { cards: [], hasMore: false };

    const pageSizePlusOne = CARDS_PAGE_SIZE + 1;
    const from = offset;
    const to = offset + pageSizePlusOne - 1;

    const { data, error } = await supabase
      .from('project_items')
      .select(getCardsSelect())
      .eq('project_id', projectId)
      .eq('company_id', user.company_id)
      .eq('list_id', listId)
      .eq('is_archived', false)
      .order('display_order', { ascending: true })
      .range(from, to);

    if (error) throw error;

    const raw = data || [];
    const hasMore = raw.length > CARDS_PAGE_SIZE;
    const page = hasMore ? raw.slice(0, CARDS_PAGE_SIZE) : raw;

    // ✅ Para projetos CRM/CRM Multi, buscar ultima_mensagem_em das conversas
    let cardsWithLastMessage = page;
    if (isCrmLikeProjectType(project?.project_type)) {
      const contactCards = page.filter(c => c.entity_type === 'contact' && c.entity_id);
      const contactIds = contactCards.map(c => c.entity_id);

      if (contactIds.length > 0) {
        const { data: conversas } = await supabase
          .from('conversas')
          .select('contato_id, ultima_mensagem_em')
          .in('contato_id', contactIds);

        const conversaMap = {};
        (conversas || []).forEach(c => {
          conversaMap[c.contato_id] = c.ultima_mensagem_em;
        });

        cardsWithLastMessage = page.map(card => ({
          ...card,
          ultima_mensagem_em: card.entity_type === 'contact' && card.entity_id
            ? conversaMap[card.entity_id] || null
            : null
        }));

        // ✅ Ordenar por ultima_mensagem_em DESC (mais recente no topo)
        // Cards com última mensagem ficam no topo, depois os sem mensagem por created_at DESC
        cardsWithLastMessage.sort((a, b) => {
          const aTime = a.ultima_mensagem_em ? new Date(a.ultima_mensagem_em).getTime() : 0;
          const bTime = b.ultima_mensagem_em ? new Date(b.ultima_mensagem_em).getTime() : 0;
          
          // Se ambos têm última mensagem, ordenar por ela
          if (aTime > 0 && bTime > 0) {
            return bTime - aTime; // DESC (mais recente primeiro)
          }
          // Se só um tem, ele fica primeiro
          if (aTime > 0) return -1;
          if (bTime > 0) return 1;
          // Se nenhum tem, ordenar por created_at DESC
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
      }
    }

    const mapped = cardsWithLastMessage.map(card => ({
      id: card.id,
      title: card.title,
      description: card.description,
      display_order: card.display_order || 0,
      labels: [],
      dueDate: card.due_date,
      priority: card.priority,
      checklist: [],
      comments: [],
      attachments: [],
      links: [],
      members: card.assigned_to || [],
      reminder: null,
      entity_type: card.entity_type,
      entity_id: card.entity_id,
      metadata: card.metadata,
      custom_fields: null,
      created_at: card.created_at,
      ultima_mensagem_em: card.ultima_mensagem_em || null
    }));

    // ✅ Reordenar novamente após mapeamento para CRM/CRM Multi (preservar ordem)
    if (isCrmLikeProjectType(project?.project_type)) {
      return { cards: reorderCardsByLastMessage(mapped), hasMore };
    }

    return { cards: mapped, hasMore };
  };

  // Carregar eventos quando mudar para visualização de calendário
  useEffect(() => {
    if (viewMode === 'calendar' && projectId) {
      loadCalendarEvents();
    }
  }, [viewMode, projectId]);

  // ✅ Carregar mesas apenas para Bar & Restaurante
  useEffect(() => {
    if (projectId && project?.project_type === 'bar_restaurant') {
      loadBarRestaurantTables();
    }
  }, [projectId, project?.project_type]);

  // ✅ Atualizar apenas a lista afetada (não recarregar tudo)
  const handleListRealtimeUpdate = (payload) => {
    if (payload.eventType === 'DELETE') {
      setLists(prev => prev.filter(l => l.id !== payload.old.id));
    } else if (payload.eventType === 'UPDATE') {
      setLists(prev => prev.map(l => 
        l.id === payload.new.id 
          ? { ...l, title: payload.new.name, color: payload.new.color, icon: payload.new.icon }
          : l
      ));
    } else if (payload.eventType === 'INSERT') {
      setLists(prev => [...prev, {
        id: payload.new.id,
        title: payload.new.name,
        color: payload.new.color,
        icon: payload.new.icon,
        cards: [],
        cards_has_more: false
      }]);
    }
  };

  // ✅ Atualizar apenas o card afetado (não recarregar tudo)
  const handleCardRealtimeUpdate = (payload) => {
    if (payload.eventType === 'DELETE') {
      setLists(prev => {
        const updated = prev.map(list => ({
          ...list,
          cards: list.cards.filter(c => c.id !== payload.old.id)
        }));

        // ✅ Reordenar por última mensagem para CRM/CRM Multi
        if (isCrmLikeProjectType(project?.project_type)) {
          return updated.map(list => ({
            ...list,
            cards: reorderCardsByLastMessage(list.cards)
          }));
        }

        return updated;
      });
    } else if (payload.eventType === 'UPDATE') {
      const newListId = payload.new.list_id;
      const cardId = payload.new.id;
      
      setLists(prev => {
        // Verificar se o card mudou de lista
        const currentList = prev.find(l => l.cards.some(c => c.id === cardId));
        const cardAlreadyInCorrectList = currentList?.id === newListId;
        
        if (cardAlreadyInCorrectList) {
          // Card está na lista correta, apenas atualizar campos
          const updated = prev.map(list => ({
            ...list,
            cards: list.cards.map(c => 
              c.id === cardId 
                ? {
                    ...c,
                    title: payload.new.title,
                    description: payload.new.description,
                    dueDate: payload.new.due_date,
                    priority: payload.new.priority,
                    display_order: payload.new.display_order
                  }
                : c
            )
          }));

          // ✅ Reordenar por última mensagem para CRM/CRM Multi
          if (isCrmLikeProjectType(project?.project_type)) {
            return updated.map(list => ({
              ...list,
              cards: reorderCardsByLastMessage(list.cards)
            }));
          }

          return updated;
        } else {
          // Card mudou de lista — remover da antiga e adicionar na nova
          const existingCard = currentList?.cards.find(c => c.id === cardId);
          if (!existingCard) return prev; // Card não encontrado, ignorar
          
          const updatedCard = {
            ...existingCard,
            title: payload.new.title,
            description: payload.new.description,
            dueDate: payload.new.due_date,
            priority: payload.new.priority,
            display_order: payload.new.display_order,
            entity_type: payload.new.entity_type,
            entity_id: payload.new.entity_id,
            metadata: payload.new.metadata || existingCard.metadata,
            assigned_to: payload.new.assigned_to
          };
          
          const updated = prev.map(list => {
            if (list.id === currentList.id) {
              return { ...list, cards: list.cards.filter(c => c.id !== cardId) };
            }
            if (list.id === newListId) {
              const newCards = [...list.cards, updatedCard].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
              return { ...list, cards: newCards };
            }
            return list;
          });

          // ✅ Reordenar por última mensagem para CRM/CRM Multi
          if (isCrmLikeProjectType(project?.project_type)) {
            return updated.map(list => ({
              ...list,
              cards: reorderCardsByLastMessage(list.cards)
            }));
          }

          return updated;
        }
      });
    } else if (payload.eventType === 'INSERT') {
      setLists(prev => {
        // Evitar duplicata se card já existe (optimistic update)
        const alreadyExists = prev.some(l => l.cards.some(c => c.id === payload.new.id));
        if (alreadyExists) return prev;
        
        const updated = prev.map(list => 
          list.id === payload.new.list_id
            ? {
                ...list,
                cards: [...list.cards, {
                  id: payload.new.id,
                  title: payload.new.title,
                  description: payload.new.description,
                  display_order: payload.new.display_order,
                  dueDate: payload.new.due_date,
                  priority: payload.new.priority,
                  entity_type: payload.new.entity_type,
                  entity_id: payload.new.entity_id,
                  metadata: payload.new.metadata,
                  assigned_to: payload.new.assigned_to,
                  created_at: payload.new.created_at,
                  ultima_mensagem_em: null
                }]
              }
            : list
        );

        // ✅ Reordenar por última mensagem para CRM/CRM Multi
        if (isCrmLikeProjectType(project?.project_type)) {
          const reordered = updated.map(list => ({
            ...list,
            cards: reorderCardsByLastMessage(list.cards)
          }));
          
          // ✅ Enviar mensagem automática quando card entra na lista (realtime INSERT)
          notifyCardMoved(payload.new.id, payload.new.list_id, null);
          
          return reordered;
        }

        return updated;
      });
    }
  };

  // ✅ Remover apenas o card do contato deletado
  const handleContactDelete = (payload) => {
    if (isCrmLikeProjectType(project?.project_type)) {
      setLists(prev => {
        const updated = prev.map(list => ({
          ...list,
          cards: list.cards.filter(card => card.entity_id !== payload.old.id)
        }));

        // ✅ Reordenar por última mensagem para CRM/CRM Multi
        return updated.map(list => ({
          ...list,
          cards: reorderCardsByLastMessage(list.cards)
        }));
      });
    }
  };

  // ✅ Reordenar cards por última mensagem (para CRM/CRM Multi)
  const reorderCardsByLastMessage = (cards) => {
    if (!cards || cards.length === 0) return cards;
    
    return [...cards].sort((a, b) => {
      const aTime = a.ultima_mensagem_em ? new Date(a.ultima_mensagem_em).getTime() : 0;
      const bTime = b.ultima_mensagem_em ? new Date(b.ultima_mensagem_em).getTime() : 0;
      
      // Se ambos têm última mensagem, ordenar por ela
      if (aTime > 0 && bTime > 0) {
        return bTime - aTime; // DESC (mais recente primeiro)
      }
      // Se só um tem, ele fica primeiro
      if (aTime > 0) return -1;
      if (bTime > 0) return 1;
      // Se nenhum tem, ordenar por created_at DESC
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  };

  // ✅ Handler para atualizações de conversas (realtime)
  const handleConversaUpdate = async (payload) => {
    if (!isCrmLikeProjectType(project?.project_type)) return;
    
    const contatoId = payload.new?.contato_id || payload.old?.contato_id;
    if (!contatoId) return;

    const novaUltimaMensagem = payload.new?.ultima_mensagem_em;

    setLists(prev => {
      return prev.map(list => ({
        ...list,
        cards: list.cards.map(card => {
          if (card.entity_type === 'contact' && card.entity_id === contatoId) {
            return { ...card, ultima_mensagem_em: novaUltimaMensagem };
          }
          return card;
        })
      })).map(list => ({
        ...list,
        cards: reorderCardsByLastMessage(list.cards)
      }));
    });
  };

  // Configurar Realtime para listas e cards
  useEffect(() => {
    if (!projectId) return;

    const user = JSON.parse(localStorage.getItem('astro_user_data'));
    if (!user?.company_id) return;

    // Subscribe para mudanças em project_lists
    const listsChannel = supabase
      .channel(`project_lists:${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_lists',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          handleListRealtimeUpdate(payload);
        }
      )
      .subscribe();

    // Subscribe para mudanças em project_items
    const itemsChannel = supabase
      .channel(`project_items:${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_items',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          handleCardRealtimeUpdate(payload);
        }
      )
      .subscribe();

    // ✅ Subscribe para deleções de contatos (sincronizar com projeto CRM)
    const contactsChannel = supabase
      .channel(`contatos:${user.company_id}`)
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'contatos',
          filter: `company_id=eq.${user.company_id}`
        },
        (payload) => {
          handleContactDelete(payload);
        }
      )
      .subscribe();

    // ✅ Subscribe para atualizações de conversas (reordenar cards por última mensagem)
    let conversasChannel = null;
    if (isCrmLikeProjectType(project?.project_type)) {
      conversasChannel = supabase
        .channel(`conversas_crm:${projectId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'conversas'
          },
          (payload) => {
            handleConversaUpdate(payload);
          }
        )
        .subscribe();
    }

    // Cleanup ao desmontar
    return () => {
      supabase.removeChannel(listsChannel);
      supabase.removeChannel(itemsChannel);
      supabase.removeChannel(contactsChannel);
      if (conversasChannel) supabase.removeChannel(conversasChannel);
    };
  }, [projectId, project?.project_type]);

  const loadProject = async () => {
    try {
      console.log('📂 Carregando projeto:', projectId);
      
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, description, project_type, color, icon, background_image, ai_agent_id, settings')
        .eq('id', projectId)
        .single();

      if (error) throw error;

      console.log('✅ Projeto carregado:', data.name, 'Tipo:', data.project_type);
      setProject(data);
      
      // ✅ Inicializar viewMode baseado no project_type usando PROJECT_TYPES
      if (isValidProjectType(data.project_type)) {
        const config = PROJECT_TYPES[data.project_type];
        setViewMode(config.defaultViewMode);
        console.log(`📺 ViewMode inicializado para ${data.project_type}:`, config.defaultViewMode);
      } else {
        console.warn('⚠️ Tipo de projeto desconhecido:', data.project_type, '- usando fallback');
        setViewMode('kanban'); // fallback
      }
    } catch (error) {
      console.error('❌ Erro ao carregar projeto:', error);
      alert('Erro ao carregar projeto: ' + error.message);
    }
  };

  const loadCalendarEvents = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!user?.company_id) return;

      console.log('📅 Carregando eventos do projeto:', projectId);

      const { data, error } = await supabase
        .from('calendar_events')
        .select('id, title, start_date, end_date, event_type, color, priority, project_id, custom_fields, contact_id, status, description, attachments')
        .eq('company_id', user.company_id)
        .eq('project_id', projectId)
        .eq('is_deleted', false)
        .order('start_date', { ascending: true });

      if (error) {
        console.error('❌ Erro na query:', error);
        throw error;
      }

      console.log('✅ Eventos carregados:', data?.length || 0, data);
      setCalendarEvents(data || []);
    } catch (error) {
      console.error('❌ Erro ao carregar eventos:', error);
    }
  };

  const loadLists = async () => {
    try {
      console.log('📋 Carregando listas do projeto:', projectId);
      
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!user?.company_id) {
        console.error('❌ Usuário sem company_id');
        return;
      }

      const crmDefaultListNames =
        project?.project_type === 'marketing_campaign'
          ? new Set([
              'Disparo pendente',
              'Disparo efetivado',
              'Em Atendimento',
              'Equipe de Vendas',
              'Vendas Efetivadas',
              'Follow-up 1',
              'Follow-up 2',
              'Follow-up 3',
            ])
          : new Set([
              'Leads Recebidos',
              'Em Atendimento',
              'Aguardando Formulário',
              'Equipe de Vendas',
              'Vendas Efetivadas',
              'Follow-up 1',
              'Follow-up 2',
              'Follow-up 3',
            ]);

      // Buscar listas
      const { data: listsData, error: listsError } = await supabase
        .from('project_lists')
        .select('id, name, color, icon, display_order')
        .eq('project_id', projectId)
        .eq('company_id', user.company_id)
        .order('display_order', { ascending: true });

      if (listsError) throw listsError;

      // Carregar primeira página (10) de cards por lista
      const listsWithCards = await Promise.all(
        (listsData || []).map(async (list) => {
          const { cards, hasMore } = await fetchCardsPageForList({ listId: list.id, offset: 0 });
          return {
            id: list.id,
            title: list.name,
            color: list.color,
            icon: list.icon,
            is_default: (isCrmLikeProjectType(project?.project_type)) ? crmDefaultListNames.has(list.name) : false,
            cards,
            cards_has_more: hasMore
          };
        })
      );

      const nextPagination = {};
      for (const l of listsWithCards) {
        nextPagination[l.id] = {
          loading: false,
          hasMore: !!l.cards_has_more,
        };
      }
      setListPagination(nextPagination);

      console.log('✅ Listas carregadas:', listsWithCards.length);
      setLists(listsWithCards);

    } catch (error) {
      console.error('❌ Erro ao carregar listas:', error);
      alert('Erro ao carregar listas: ' + error.message);
    }
  };

  const loadBarRestaurantTables = async () => {
    try {
      console.log('[KanbanBoard] Carregando mesas para projectId:', projectId);
      const response = await fetch(`/api/bar-restaurant/tables/${projectId}`);
      console.log('[KanbanBoard] Resposta do servidor:', response.status, response.ok);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('[KanbanBoard] Erro na resposta:', errorData);
        throw new Error('Erro ao buscar mesas');
      }
      
      const data = await response.json();
      console.log('[KanbanBoard] Dados recebidos:', data);
      
      if (data.success) {
        console.log('[KanbanBoard] Mesas carregadas com sucesso:', data.tables?.length || 0);
        setBarRestaurantTables(data.tables || []);
      } else {
        console.error('[KanbanBoard] Resposta sem sucesso:', data);
      }
    } catch (error) {
      console.error('[KanbanBoard] Erro ao carregar mesas:', error);
    }
  };

  const loadMoreCardsForList = async (listId) => {
    try {
      setListPagination((prev) => {
        const cur = prev[listId] || { loading: false, hasMore: true };
        if (cur.loading || !cur.hasMore) return prev;
        return { ...prev, [listId]: { ...cur, loading: true } };
      });

      const targetList = lists.find(l => l.id === listId);
      const offset = targetList?.cards?.length || 0;

      const { cards: newCards, hasMore } = await fetchCardsPageForList({ listId, offset });

      setLists((prev) =>
        prev.map((l) => {
          if (l.id !== listId) return l;
          return {
            ...l,
            cards: [...(l.cards || []), ...(newCards || [])],
            cards_has_more: hasMore
          };
        })
      );

      setListPagination((prev) => ({
        ...prev,
        [listId]: {
          loading: false,
          hasMore: !!hasMore,
        }
      }));
    } catch (error) {
      console.error('❌ Erro ao carregar mais cards:', error);
      setListPagination((prev) => ({
        ...prev,
        [listId]: {
          ...(prev[listId] || {}),
          loading: false,
        }
      }));
    }
  };

  const handleListScroll = (listId, e) => {
    try {
      const el = e.currentTarget;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      const paging = listPagination[listId];
      const listObj = lists.find(l => l.id === listId);
      const hasMore = paging?.hasMore ?? !!listObj?.cards_has_more;
      const isLoading = !!paging?.loading;

      if (remaining < 120 && hasMore && !isLoading) {
        loadMoreCardsForList(listId);
      }
    } catch (_) {}
  };

  const handleAddList = async () => {
    try {
      // ✅ Opção B (CRM): criar a lista primeiro e abrir o modal depois
      if (isCrmLikeProjectType(project?.project_type)) {
        const user = JSON.parse(localStorage.getItem('astro_user_data'));
        if (!user?.company_id) return;

        const defaultListData = {
          title: 'Nova Lista',
          color: '#b43bea',
          icon: 'list'
        };

        const { data: createdList, error } = await supabase
          .from('project_lists')
          .insert({
            project_id: projectId,
            company_id: user.company_id,
            name: defaultListData.title,
            color: defaultListData.color,
            icon: defaultListData.icon,
            display_order: lists.length,
            created_by: user.id
          })
          .select('id, name, color, icon')
          .single();

        if (error) throw error;

        // ✅ Optimistic update: adicionar lista ao estado local imediatamente
        const newList = {
          id: createdList.id,
          title: createdList.name,
          color: createdList.color,
          icon: createdList.icon,
          display_order: lists.length,
          cards: [],
          cards_has_more: false
        };
        setLists(prev => [...prev, newList]);

        // Abrir modal de configuração imediatamente
        setEditingListData({
          id: createdList.id,
          title: createdList.name,
          color: createdList.color,
          icon: createdList.icon
        });
        setShowListConfigModal(true);
        return;
      }

      // Comportamento antigo para outros tipos de projeto
      setEditingListData(null);
      setShowListConfigModal(true);
    } catch (error) {
      console.error('❌ Erro ao criar lista:', error);
      alert('Erro ao criar lista: ' + error.message);
    }
  };

  const handleSaveListConfig = async (listData) => {
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!user?.company_id) return;

      if (editingListData) {
        // ✅ Optimistic update: atualizar UI imediatamente
        setLists(prev => prev.map(l => 
          l.id === editingListData.id 
            ? { ...l, title: listData.title, color: listData.color, icon: listData.icon }
            : l
        ));

        // Salvar no Supabase em background
        supabase
          .from('project_lists')
          .update({ name: listData.title, color: listData.color, icon: listData.icon })
          .eq('id', editingListData.id)
          .then(({ error }) => { if (error) console.error('❌ Erro ao salvar lista:', error); });
      } else {
        // Criar nova lista
        const { data: created, error } = await supabase
          .from('project_lists')
          .insert({
            project_id: projectId,
            company_id: user.company_id,
            name: listData.title,
            color: listData.color,
            icon: listData.icon,
            display_order: lists.length,
            created_by: user.id
          })
          .select('id, name, color, icon')
          .single();

        if (error) throw error;

        // ✅ Optimistic update
        setLists(prev => [...prev, {
          id: created.id,
          title: created.name,
          color: created.color,
          icon: created.icon,
          display_order: lists.length,
          cards: [],
          cards_has_more: false
        }]);
      }

      setShowListConfigModal(false);
      setEditingListData(null);
    } catch (error) {
      console.error('❌ Erro ao salvar lista:', error);
      alert('Erro ao salvar lista: ' + error.message);
    }
  };

  // ✅ Handler para quando o modal CRM salva info da lista (nome/cor/ícone)
  // O modal já salvou no Supabase, aqui só atualizamos o estado local
  const handleCRMListInfoUpdate = (updatedList) => {
    if (!updatedList?.id) return;
    setLists(prev => prev.map(l => 
      l.id === updatedList.id 
        ? { ...l, title: updatedList.title, color: updatedList.color, icon: updatedList.icon }
        : l
    ));
  };

  const handleAddCard = (listId) => {
    setSelectedListForCard(listId);
    setShowAddCardTypeModal(true);
  };

  const handleSelectCardType = (cardType) => {
    setShowAddCardTypeModal(false);
    
    if (cardType === 'contact') {
      // Abrir modal de seleção de contato
      setShowSelectContactModal(true);
    } else {
      // Criar card normal
      createNormalCard(selectedListForCard);
    }
  };

  const createNormalCard = async (listId) => {
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!user?.company_id) return;

      // ✅ Incrementar display_order de todos os cards existentes na lista
      // Isso garante que o novo card (com display_order = 0) fique no topo
      const existingCards = lists.find(l => l.id === listId)?.cards || [];
      if (existingCards.length > 0) {
        const updatePromises = existingCards.map(card => 
          supabase
            .from('project_items')
            .update({ display_order: (card.display_order || 0) + 1 })
            .eq('id', card.id)
        );
        await Promise.all(updatePromises);
      }

      // Criar novo card com display_order = 0 (ficará no topo)
      const { data, error } = await supabase
        .from('project_items')
        .insert({
          list_id: listId,
          project_id: projectId,
          company_id: user.company_id,
          title: 'Novo Card',
          description: '',
          display_order: 0, // ✅ Novo card sempre no topo
          list_entered_at: new Date().toISOString(),
          created_by: user.id
        })
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Card criado no topo da lista');
      loadLists();
      
      // Abrir modal para editar
      setSelectedCard({
        id: data.id,
        title: data.title,
        description: data.description,
        labels: [],
        checklist: [],
        comments: [],
        attachments: [],
        company_id: user.company_id  // ✅ Incluir company_id
      });
      setSelectedCardListId(listId);
    } catch (error) {
      console.error('❌ Erro ao criar card:', error);
      alert('Erro ao criar card: ' + error.message);
    }
  };

  const handleSelectContact = async (contact) => {
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!user?.company_id) return;

      setShowSelectContactModal(false);

      // ✅ Verificar se contato já existe neste projeto (evita duplicação)
      const { data: existingCard, error: checkError } = await supabase
        .from('project_items')
        .select('id')
        .eq('project_id', projectId)
        .eq('company_id', user.company_id)
        .eq('entity_type', 'contact')
        .eq('entity_id', contact.id)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingCard) {
        alert(`❌ Este contato (${contact.nome || contact.numero}) já existe neste projeto!`);
        return;
      }

      // ✅ Incrementar display_order de todos os cards existentes na lista
      // Isso garante que o novo card (com display_order = 0) fique no topo
      const existingCards = lists.find(l => l.id === selectedListForCard)?.cards || [];
      if (existingCards.length > 0) {
        const updatePromises = existingCards.map(card => 
          supabase
            .from('project_items')
            .update({ display_order: (card.display_order || 0) + 1 })
            .eq('id', card.id)
        );
        await Promise.all(updatePromises);
      }

      // Criar card vinculado ao contato com display_order = 0 (ficará no topo)
      const { data, error } = await supabase
        .from('project_items')
        .insert({
          list_id: selectedListForCard,
          project_id: projectId,
          company_id: user.company_id,
          entity_type: 'contact',
          entity_id: contact.id,
          title: contact.nome || contact.numero || 'Contato',
          description: `Contato adicionado manualmente em ${new Date().toLocaleString('pt-BR')}`,
          display_order: 0, // ✅ Novo card sempre no topo
          list_entered_at: new Date().toISOString(),
          status: 'active',
          priority: 'normal',
          metadata: {
            source: 'manual',
            added_by: user.id,
            added_by_name: user.nome || user.email,
            added_at: new Date().toISOString()
          },
          created_by: user.id
        })
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Card de contato criado no topo da lista');
      loadLists();
    } catch (error) {
      console.error('❌ Erro ao criar card de contato:', error);
      alert('Erro ao criar card de contato: ' + error.message);
    }
  };

  const handleDeleteList = async (listId) => {
    try {
      // ✅ Verificar se a lista é padrão (não pode ser deletada)
      const listToDelete = lists.find(l => l.id === listId);
      if (listToDelete && listToDelete.is_default) {
        alert('❌ Listas padrão do projeto CRM não podem ser deletadas!');
        setShowDeleteModal(null);
        return;
      }

      // Deletar todos os cards da lista primeiro
      const { error: cardsError } = await supabase
        .from('project_items')
        .delete()
        .eq('list_id', listId);

      if (cardsError) throw cardsError;

      // Deletar a lista
      const { error } = await supabase
        .from('project_lists')
        .delete()
        .eq('id', listId);

      if (error) throw error;

      console.log('✅ Lista deletada');
      loadLists();
      setShowDeleteModal(null);
    } catch (error) {
      console.error('❌ Erro ao deletar lista:', error);
      alert('Erro ao deletar lista: ' + error.message);
    }
  };

  const handleDeleteCard = async (listId, cardId) => {
    if (!confirm('Deseja realmente excluir este card?')) return;

    try {
      const { error } = await supabase
        .from('project_items')
        .delete()
        .eq('id', cardId);

      if (error) throw error;

      console.log('✅ Card excluído');
      loadLists();
    } catch (error) {
      console.error('❌ Erro ao excluir card:', error);
      alert('Erro ao excluir card: ' + error.message);
    }
  };

  const handleOpenCard = async (card, listId) => {
    // Se for card de pedido Bar & Restaurante, abrir modal específico
    if (project?.project_type === 'bar_restaurant' && card.entity_type === 'bar_restaurant_order') {
      setSelectedBarRestaurantOrder(card);
      setShowBarRestaurantOrderModal(true);
      return;
    }
    
    // ✅ Carregar campos pesados apenas quando abrir o modal
    const fullDetails = await fetchCardFullDetails(card.id);
    const cardWithDetails = {
      ...card,
      checklist: fullDetails?.checklist || [],
      comments: fullDetails?.comments || [],
      attachments: fullDetails?.attachments || [],
      links: fullDetails?.links || [],
      metadata: fullDetails?.metadata || null,
      custom_fields: fullDetails?.custom_fields || null
    };
    
    setSelectedCard(cardWithDetails);
    setSelectedCardListId(listId);
  };

  const handleOpenTable = (table) => {
    setSelectedBarRestaurantTable(table);
    setShowBarRestaurantTableModal(true);
  };

  const handleUpdateCard = async (listId, updatedCard, oldListId = null) => {
    try {
      const updateData = {
        title: updatedCard.title,
        description: updatedCard.description,
        priority: updatedCard.priority,
        due_date: updatedCard.dueDate,
        checklist: updatedCard.checklist,
        attachments: updatedCard.attachments,
        links: updatedCard.links || [],
        comments: updatedCard.comments || [],
        assigned_to: updatedCard.members || [],
        reminder: updatedCard.reminder || null
      };

      console.log('💾 Salvando card:', {
        id: updatedCard.id,
        links: updateData.links,
        comments: updateData.comments,
        members: updateData.assigned_to
      });

      // Se mudou de lista
      if (oldListId && oldListId !== listId) {
        updateData.list_id = listId;
        updateData.list_entered_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('project_items')
        .update(updateData)
        .eq('id', updatedCard.id);

      if (error) throw error;

      console.log('✅ Card atualizado com sucesso');
      console.log('📅 Card dueDate:', updatedCard.dueDate);

      // Sincronizar com calendar_events se tiver data
      if (updatedCard.dueDate) {
        console.log('📅 Iniciando sincronização com calendário...');
        await syncCardToCalendar(updatedCard);
      } else {
        console.log('⚠️ Card sem data de vencimento, não sincronizando');
      }

      await loadLists();
      await loadCalendarEvents(); // Recarregar eventos do calendário
      
      // Forçar re-render do calendário se estiver na visualização de calendário
      if (viewMode === 'calendar') {
        console.log('📅 Forçando atualização do calendário...');
        setCurrentDate(new Date(currentDate)); // Força re-render
      }
      
      // Atualizar card selecionado
      if (selectedCard?.id === updatedCard.id) {
        setSelectedCard(updatedCard);
        if (oldListId && oldListId !== listId) {
          setSelectedCardListId(listId);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar card:', error);
      alert('Erro ao atualizar card: ' + error.message);
    }
  };

  // Função para sincronizar card com calendar_events
  const syncCardToCalendar = async (card) => {
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      console.log('📅 User data:', { company_id: user?.company_id, user_id: user?.id });
      console.log('📅 Card data:', { id: card.id, title: card.title, dueDate: card.dueDate });
      console.log('📅 Project data:', { id: projectId, color: project.color });
      
      if (!user?.company_id || !card.dueDate) {
        console.log('⚠️ Faltam dados para sincronizar:', { 
          hasCompanyId: !!user?.company_id, 
          hasDueDate: !!card.dueDate 
        });
        return;
      }

      console.log('📅 Sincronizando card com calendário:', card.title);

      // Verificar se já existe um evento para este card (usando project_item_id)
      const { data: existingEvents, error: searchError } = await supabase
        .from('calendar_events')
        .select('id')
        .eq('project_item_id', card.id)
        .eq('is_deleted', false)
        .limit(1);

      if (searchError) {
        console.error('❌ Erro ao buscar evento existente:', searchError);
        throw searchError;
      }

      console.log('📅 Eventos existentes encontrados:', existingEvents?.length || 0);

      // Preparar data no formato correto (timestamp)
      const dueDateTime = new Date(card.dueDate);
      console.log('📅 Data convertida:', dueDateTime.toISOString());
      
      const eventData = {
        title: card.title,
        description: card.description || '',
        start_date: dueDateTime.toISOString(),
        end_date: dueDateTime.toISOString(),
        event_type: 'task',
        all_day: true,
        color: project?.color || '#06b6d4',
        project_id: projectId,
        project_item_id: card.id,
        company_id: user.company_id,
        priority: card.priority || 'normal',
        status: 'scheduled'
      };

      console.log('📅 Dados do evento a salvar:', eventData);
      
      // Validar que project_id não é null
      if (!eventData.project_id) {
        console.error('⚠️ ATENÇÃO: project_id está null!', { projectId, project });
        throw new Error('project_id não pode ser null');
      }

      if (existingEvents && existingEvents.length > 0) {
        // Atualizar evento existente
        console.log('📅 Atualizando evento existente:', existingEvents[0].id);
        const { data: updateData, error: updateError } = await supabase
          .from('calendar_events')
          .update({
            ...eventData,
            updated_by: user.id,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingEvents[0].id)
          .select();

        if (updateError) {
          console.error('❌ Erro ao atualizar evento:', updateError);
          throw updateError;
        }
        console.log('✅ Evento atualizado no calendário:', updateData);
      } else {
        // Criar novo evento
        console.log('📅 Criando novo evento no calendário...');
        const { data: insertData, error: insertError } = await supabase
          .from('calendar_events')
          .insert({
            ...eventData,
            created_by: user.id,
            organizer_id: user.id
          })
          .select();

        if (insertError) {
          console.error('❌ Erro ao inserir evento:', insertError);
          throw insertError;
        }
        console.log('✅ Evento criado no calendário:', insertData);
      }
    } catch (error) {
      console.error('❌ Erro ao sincronizar com calendário:', error);
      console.error('Detalhes do erro:', error.message);
      console.error('Stack:', error.stack);
      // Não bloquear a atualização do card se falhar a sincronização
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const activeId = active.id;
    
    // ✅ Verificar se é uma lista
    const draggedList = lists.find(l => l.id === activeId);
    if (draggedList) {
      setActiveList(draggedList);
      return;
    }
    
    // Verificar se é um card
    for (const list of lists) {
      const card = list.cards.find(c => c.id === activeId);
      if (card) {
        setActiveCard(card);
        break;
      }
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    setActiveCard(null);
    setActiveList(null);
    
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    try {
      // ✅ CASO 0: Reordenação de listas
      const draggedList = lists.find(l => l.id === activeId);
      const overList = lists.find(l => l.id === overId);
      
      if (draggedList && overList) {
        const oldIndex = lists.findIndex(l => l.id === activeId);
        const newIndex = lists.findIndex(l => l.id === overId);
        if (oldIndex === newIndex) return;
        
        // ✅ Optimistic update: atualizar UI instantaneamente
        const reorderedLists = arrayMove(lists, oldIndex, newIndex);
        setLists(reorderedLists.map((list, index) => ({ ...list, display_order: index })));
        
        // Salvar no Supabase em background
        const updates = reorderedLists.map((list, index) => ({ id: list.id, display_order: index }));
        Promise.all(updates.map(u => 
          supabase.from('project_lists').update({ display_order: u.display_order }).eq('id', u.id)
        )).catch(err => console.error('❌ Erro ao salvar reordenação de listas:', err));
        return;
      }

      // ✅ CASO 1: Reordenação/Movimentação de cards
      let sourceListId = null;
      let sourceCard = null;
      let sourceList = null;
      
      for (const list of lists) {
        const card = list.cards.find(c => c.id === activeId);
        if (card) {
          sourceListId = list.id;
          sourceCard = card;
          sourceList = list;
          break;
        }
      }

      if (!sourceListId || !sourceCard) return;

      // Encontrar lista de destino
      let targetListId = null;
      let targetList = null;
      let overCard = null;
      
      if (lists.some(l => l.id === overId)) {
        targetListId = overId;
        targetList = lists.find(l => l.id === overId);
      } else {
        for (const list of lists) {
          const card = list.cards.find(c => c.id === overId);
          if (card) {
            targetListId = list.id;
            targetList = list;
            overCard = card;
            break;
          }
        }
      }

      if (!targetListId || !targetList) return;

      // CASO 1: Reordenação dentro da mesma lista
      if (sourceListId === targetListId) {
        const cards = [...sourceList.cards];
        const oldIndex = cards.findIndex(c => c.id === activeId);
        const newIndex = overCard ? cards.findIndex(c => c.id === overId) : cards.length;
        if (oldIndex === newIndex) return;
        
        // ✅ Optimistic update
        const reorderedCards = arrayMove(cards, oldIndex, newIndex).map((card, index) => ({ ...card, display_order: index }));
        setLists(prev => prev.map(l => l.id === sourceListId ? { ...l, cards: reorderedCards } : l));
        
        // Salvar no Supabase em background
        Promise.all(reorderedCards.map(c => 
          supabase.from('project_items').update({ display_order: c.display_order }).eq('id', c.id)
        )).catch(err => console.error('❌ Erro ao salvar reordenação de cards:', err));
        return;
      }

      // CASO 2: Mover para outra lista
      // ⛔ BLOQUEAR: cards do tipo gas_distributor_new_client não podem mudar de lista
      if (sourceCard.entity_type === 'gas_distributor_new_client' && sourceListId !== targetListId) {
        console.log('⛔ Card gas_distributor_new_client não pode ser movido para outra lista');
        return; // Ignorar o drop
      }

      let newDisplayOrder = 0;
      let updatedTargetCards = [...targetList.cards];
      
      if (overCard) {
        const overIndex = updatedTargetCards.findIndex(c => c.id === overId);
        newDisplayOrder = overIndex >= 0 ? overIndex : updatedTargetCards.length;
        // Inserir na posição correta
        updatedTargetCards.splice(newDisplayOrder, 0, { ...sourceCard, display_order: newDisplayOrder });
      } else {
        newDisplayOrder = updatedTargetCards.length;
        updatedTargetCards.push({ ...sourceCard, display_order: newDisplayOrder });
      }
      
      // Recalcular display_order de todos os cards da lista destino
      updatedTargetCards = updatedTargetCards.map((card, index) => ({ ...card, display_order: index }));
      
      // Remover card da lista de origem
      const updatedSourceCards = sourceList.cards.filter(c => c.id !== activeId);

      // ✅ Optimistic update: mover card instantaneamente na UI
      setLists(prev => prev.map(l => {
        if (l.id === sourceListId) return { ...l, cards: updatedSourceCards };
        if (l.id === targetListId) return { ...l, cards: updatedTargetCards };
        return l;
      }));

      // Salvar no Supabase em background
      const agora = new Date().toISOString();
      const updateData = { 
        list_id: targetListId,
        display_order: newDisplayOrder
      };

      if (sourceListId !== targetListId) {
        updateData.list_entered_at = agora;
      }

      // Se for card de pedido Bar & Restaurante, atualizar status e histórico
      if (project?.project_type === 'bar_restaurant' && sourceCard.entity_type === 'bar_restaurant_order') {
        const novaLista = targetList.title;
        const listNameToStatus = {
          'Pedidos em aberto': 'pedidos_em_aberto',
          'Em Produção': 'em_producao',
          'Pronto': 'pronto',
          'Entregue': 'entregue',
          'Cancelado': 'cancelado'
        };
        
        const novoStatus = listNameToStatus[novaLista] || 'pedidos_em_aberto';
        
        updateData.custom_fields = {
          ...sourceCard.custom_fields,
          status_atual: novoStatus,
          timestamp_entrada_lista: agora
        };

        // Atualizar bar_restaurant_orders em background
        supabase
          .from('bar_restaurant_orders')
          .select('historico_movimentacoes')
          .eq('id', sourceCard.entity_id)
          .single()
          .then(({ data: orderData }) => {
            const historicoAtual = orderData?.historico_movimentacoes || [];
            return supabase
              .from('bar_restaurant_orders')
              .update({
                status_atual: novoStatus,
                timestamp_entrada_lista: agora,
                historico_movimentacoes: [...historicoAtual, { lista: novaLista, timestamp: agora, acao: 'movido' }]
              })
              .eq('id', sourceCard.entity_id);
          })
          .catch(err => console.error('❌ Erro ao atualizar pedido:', err));
      }

      // Salvar movimentação do card + reordenação dos cards destino em background
      const savePromises = [
        supabase.from('project_items').update(updateData).eq('id', activeId),
        ...updatedTargetCards
          .filter(c => c.id !== activeId)
          .map(c => supabase.from('project_items').update({ display_order: c.display_order }).eq('id', c.id))
      ];
      
      Promise.all(savePromises).catch(err => console.error('❌ Erro ao salvar movimentação:', err));
      
      // Notificar backend para processar automações de entrada na lista
      // (CRM e Gas Distributor usam o mesmo endpoint de card-moved)
      if (
        isCrmLikeProjectType(project?.project_type) ||
        project?.project_type === 'gas_distributor'
      ) {
        notifyCardMoved(activeId, targetListId, sourceListId);
      }
      
      // ✅ Notificar grupo WhatsApp quando card for movido para "Em Entrega" no Gas Distributor
      if (project?.project_type === 'gas_distributor' && targetList?.title === 'Em Entrega') {
        notifyGasDistributorEmEntrega(sourceCard, targetListId);
      }
      
      // ✅ Notificar grupo WhatsApp quando card for movido para "Entregue" no Gas Distributor
      if (project?.project_type === 'gas_distributor' && targetList?.title === 'Entregue') {
        notifyGasDistributorEntregue(sourceCard, targetListId);
      }
    } catch (error) {
      console.error('❌ Erro ao mover card:', error);
      // Em caso de erro, recarregar para sincronizar
      loadLists();
    }
  };

  /**
   * Notifica o backend que um card foi movido
   * Dispara mensagem automática se configurada na lista de destino
   */
  const notifyCardMoved = async (cardId, newListId, oldListId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.warn('⚠️ Sessão não encontrada para notificar movimentação');
        return;
      }

      console.log('📨 Notificando backend sobre movimentação do card...');
      
      const response = await fetch(API_ENDPOINTS.CRM_CARD_MOVED, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          cardId,
          newListId,
          oldListId,
          reason: 'manual'
        })
      });

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Backend notificado:', result);
        if (result.autoMessage?.success) {
          console.log('📨 Mensagem automática enviada!');
        }
      } else {
        console.warn('⚠️ Erro ao notificar backend:', result.error);
      }
      
      // ✅ NOVO: Enviar notificação em grupo se configurada
      await sendGroupNotification(cardId, newListId);
    } catch (error) {
      console.error('❌ Erro ao notificar backend:', error);
      // Não bloquear a UI se falhar
    }
  };

  /**
   * ✅ NOVO: Envia notificação em grupo WhatsApp quando card entra na lista
   */
  const sendGroupNotification = async (cardId, listId) => {
    try {
      // Buscar configuração da lista
      const { data: listConfig } = await supabase
        .from('project_lists')
        .select('metadata')
        .eq('id', listId)
        .single();

      const groupNotif = listConfig?.metadata?.group_notification;
      
      if (!groupNotif?.enabled || !groupNotif?.group_jid) {
        console.log('📋 Notificação em grupo não está ativada para esta lista');
        return;
      }

      // Buscar dados do card
      const { data: card } = await supabase
        .from('project_items')
        .select('entity_id, title')
        .eq('id', cardId)
        .single();

      if (!card?.entity_id) {
        console.warn('⚠️ Card não possui entity_id');
        return;
      }

      // Buscar dados do contato
      const { data: contato } = await supabase
        .from('contatos')
        .select('nome, primeiro_nome, telefone, empresa')
        .eq('id', card.entity_id)
        .single();

      if (!contato) {
        console.warn('⚠️ Contato não encontrado');
        return;
      }

      // Substituir variáveis na mensagem
      let mensagem = groupNotif.message || '';
      mensagem = mensagem.replace(/\{\{nome\}\}/gi, contato.nome || '');
      mensagem = mensagem.replace(/\{\{primeiro_nome\}\}/gi, contato.primeiro_nome || '');
      mensagem = mensagem.replace(/\{\{telefone\}\}/gi, contato.telefone || '');
      mensagem = mensagem.replace(/\{\{empresa\}\}/gi, contato.empresa || '');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.warn('⚠️ Sessão não encontrada para enviar notificação em grupo');
        return;
      }

      console.log('📢 Enviando notificação em grupo...');

      const response = await fetch(`${BACKEND_NODE_URL}/api/crm-followup/send-group-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          connectionId: groupNotif.connection_id,
          groupJid: groupNotif.group_jid,
          message: mensagem,
          contactData: {
            nome: contato.nome,
            telefone: contato.telefone,
            empresa: contato.empresa
          }
        })
      });

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Notificação em grupo enviada!');
      } else {
        console.warn('⚠️ Erro ao enviar notificação em grupo:', result.error);
      }
    } catch (error) {
      console.error('❌ Erro ao enviar notificação em grupo:', error);
      // Não bloquear a UI se falhar
    }
  };

  /**
   * Notifica o grupo WhatsApp quando card é movido para "Em Entrega" no Gas Distributor
   */
  const notifyGasDistributorEmEntrega = async (card, listId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.warn('⚠️ Sessão não encontrada para notificar entrega');
        return;
      }

      // Buscar configuração da lista
      const { data: listConfig } = await supabase
        .from('project_lists')
        .select('metadata')
        .eq('id', listId)
        .single();

      const deliveryNotification = listConfig?.metadata?.delivery_notification;
      
      if (!deliveryNotification?.enabled) {
        console.log('📋 Notificação para Em Entrega não está ativada');
        return;
      }

      console.log('🚚 Notificando grupo sobre saída para entrega...');

      // Buscar nome do usuário logado
      const { data: userData } = await supabase.auth.getUser();
      const userName = userData?.user?.user_metadata?.name || userData?.user?.email || 'Usuário';

      // Chamar endpoint para enviar notificação
      const response = await fetch('/api/gas-distributor/notify-delivery', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          cardId: card.id,
          projectId: projectId,
          listId: listId,
          userName: userName,
          customMessage: deliveryNotification.message,
          notifyClient: deliveryNotification.notify_client || false,
          clientMessage: deliveryNotification.client_message || ''
        })
      });

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Notificação de entrega enviada para o grupo');
      } else {
        console.warn('⚠️ Erro ao enviar notificação:', result.error);
      }
    } catch (error) {
      console.error('❌ Erro ao notificar entrega:', error);
    }
  };

  /**
   * Notifica o grupo WhatsApp quando card é movido para "Entregue" no Gas Distributor
   */
  const notifyGasDistributorEntregue = async (card, listId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.warn('⚠️ Sessão não encontrada para notificar entrega concluída');
        return;
      }

      // Buscar configuração da lista
      const { data: listConfig } = await supabase
        .from('project_lists')
        .select('metadata')
        .eq('id', listId)
        .single();

      const deliveredNotification = listConfig?.metadata?.delivered_notification;
      
      if (!deliveredNotification?.enabled) {
        console.log('📋 Notificação para Entregue não está ativada');
        return;
      }

      console.log('✅ Notificando grupo sobre entrega concluída...');

      // Buscar nome do usuário logado
      const { data: userData } = await supabase.auth.getUser();
      const userName = userData?.user?.user_metadata?.name || userData?.user?.email || 'Usuário';

      // Chamar endpoint para enviar notificação
      const response = await fetch('/api/gas-distributor/notify-delivered', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          cardId: card.id,
          projectId: projectId,
          listId: listId,
          userName: userName,
          customMessage: deliveredNotification.message,
          notifyClient: deliveredNotification.notify_client || false,
          clientMessage: deliveredNotification.client_message || ''
        })
      });

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Notificação de entrega concluída enviada para o grupo');
      } else {
        console.warn('⚠️ Erro ao enviar notificação:', result.error);
      }
    } catch (error) {
      console.error('❌ Erro ao notificar entrega concluída:', error);
    }
  };

  // ✅ NOVO: Função para minimizar/expandir listas
  const toggleListMinimized = (listId) => {
    setMinimizedLists(prev => ({
      ...prev,
      [listId]: !prev[listId]
    }));
  };

  // Funções auxiliares do calendário
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getEventsForDate = (date) => {
    return calendarEvents.filter(event => {
      const eventDate = new Date(event.start_date);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setSelectedEvent(null);
    setShowDayTimeline(true); // Abre a timeline do dia
  };

  const handleEventClick = (event, e) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleSaveEvent = async (eventData) => {
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      
      if (selectedEvent) {
        // Atualizar evento existente
        const { error } = await supabase
          .from('calendar_events')
          .update({
            ...eventData,
            project_id: projectId,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedEvent.id);

        if (error) throw error;
      } else {
        // Criar novo evento
        const { error } = await supabase
          .from('calendar_events')
          .insert({
            ...eventData,
            company_id: user.company_id,
            project_id: projectId,
            created_by: user.id
          });

        if (error) throw error;
      }

      loadCalendarEvents();
      setShowEventModal(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Erro ao salvar evento:', error);
      alert('Erro ao salvar evento: ' + error.message);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      
      const { error } = await supabase
        .from('calendar_events')
        .update({
          is_deleted: true,
          deleted_at: new Date().toISOString(),
          deleted_by: user.id
        })
        .eq('id', eventId);

      if (error) throw error;

      loadCalendarEvents();
      setShowEventModal(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Erro ao deletar evento:', error);
      alert('Erro ao deletar evento: ' + error.message);
    }
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
    const days = [];
    
    // Dias do mês anterior (vazios)
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Dias do mês atual
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayEvents = getEventsForDate(date);
      const isToday = date.toDateString() === new Date().toDateString();
      
      days.push(
        <div
          key={day}
          className={`calendar-day ${isToday ? 'today' : ''}`}
          onClick={() => handleDateClick(date)}
        >
          <div className="day-number">{day}</div>
          <div className="day-events">
            {dayEvents.slice(0, 3).map(event => (
              <div
                key={event.id}
                className="calendar-event"
                style={{ backgroundColor: event.color || project.color }}
                onClick={(e) => handleEventClick(event, e)}
                title={event.title}
              >
                <AppIcon name={event.event_type === 'meeting' ? 'videocam' : 'event'} />
                <span className="event-title">{event.title}</span>
              </div>
            ))}
            {dayEvents.length > 3 && (
              <div className="more-events">+{dayEvents.length - 3} mais</div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="calendar-container">
        <div className="calendar-header-controls">
          <div className="calendar-nav">
            <button className="btn-calendar" onClick={() => changeMonth(-1)}>
              <AppIcon name="chevron_left" />
            </button>
            <h2>{currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</h2>
            <button className="btn-calendar" onClick={() => changeMonth(1)}>
              <AppIcon name="chevron_right" />
            </button>
          </div>
          <div className="calendar-actions">
            <button className="btn-calendar" onClick={goToToday}>
              <AppIcon name="today" />
              Hoje
            </button>
            <button className="btn-calendar btn-new-event" onClick={() => { setSelectedEvent(null); setSelectedDate(new Date()); setShowEventModal(true); }}>
              <AppIcon name="add" />
              Novo Evento
            </button>
          </div>
        </div>
        <div className="calendar-weekdays">
          <div className="weekday-label">Dom</div>
          <div className="weekday-label">Seg</div>
          <div className="weekday-label">Ter</div>
          <div className="weekday-label">Qua</div>
          <div className="weekday-label">Qui</div>
          <div className="weekday-label">Sex</div>
          <div className="weekday-label">Sáb</div>
        </div>
        <div className="calendar-grid">
          {days}
        </div>
      </div>
    );
  };

  if (!project) {
    return (
      <div className="kanban-container">
        <LoadingSpinner message="Carregando projeto..." />
      </div>
    );
  }

  return (
    <div className="kanban-container">
      {/* Header do Projeto */}
      <div className="kanban-header" style={{ borderBottomColor: project.color }}>
        <div className="header-left">
          <button className="btn-back" onClick={onBack}>
            <AppIcon name="arrow_back" />
          </button>
          <div className="project-header-info">
            <div className="project-icon-small" style={{ backgroundColor: project.color }}>
              <AppIcon name={project.icon} />
            </div>
            <div>
              <h1>{project.name}</h1>
            </div>
          </div>
        </div>
        <div className="header-actions">
          {/* Botões de Visualização - Renderizados dinamicamente conforme PROJECT_TYPES */}
          <div className="view-mode-buttons">
            {/* Botão Buscar Contato (CRM) */}
            {PROJECT_TYPES[project?.project_type]?.headerButtons?.includes('search_contact') && (
              <button 
                className="btn-view-mode"
                onClick={() => setShowSearchContactModal(true)}
                title="Buscar Contato"
              >
                <AppIcon name="person_search" />
                <span>Buscar Contato</span>
              </button>
            )}

            {/* Botão Novo Cliente (Gas Distributor) */}
            {PROJECT_TYPES[project?.project_type]?.headerButtons?.includes('new_client') && (
              <button
                className="btn-view-mode"
                onClick={() => setShowNewManualClientModal(true)}
                title="Cadastrar Novo Cliente"
              >
                <AppIcon name="person_add" />
                <span>Novo Cliente</span>
              </button>
            )}
            
            {/* Botão Kanban */}
            {PROJECT_TYPES[project?.project_type]?.headerButtons?.includes('kanban') && (
              <button 
                className={`btn-view-mode ${viewMode === 'kanban' ? 'active' : ''}`}
                onClick={() => setViewMode('kanban')}
                title="Visualização Kanban"
              >
                <AppIcon name="view_week" />
                <span>Kanban</span>
              </button>
            )}
            
            {/* Botão Mesas (Bar & Restaurante) */}
            {PROJECT_TYPES[project?.project_type]?.headerButtons?.includes('tables') && (
              <button 
                className={`btn-view-mode ${viewMode === 'tables' ? 'active' : ''}`}
                onClick={() => setViewMode('tables')}
                title="Visualização de Mesas"
              >
                <AppIcon name="table_restaurant" />
                <span>Mesas</span>
              </button>
            )}
            
            {/* Botão Pedidos (Bar & Restaurante) */}
            {PROJECT_TYPES[project?.project_type]?.headerButtons?.includes('orders') && (
              <button 
                className={`btn-view-mode ${viewMode === 'orders' ? 'active' : ''}`}
                onClick={() => setViewMode('orders')}
                title="Visualização de Pedidos"
              >
                <AppIcon name="receipt_long" />
                <span>Pedidos</span>
              </button>
            )}

            {/* Botão Fechar Turno (Bar & Restaurante) */}
            {PROJECT_TYPES[project?.project_type]?.headerButtons?.includes('close_shift') && (
              <button
                className="btn-view-mode"
                onClick={() => setShowCloseShiftModal(true)}
                title="Fechar Turno"
              >
                <AppIcon name="stop_circle" />
                <span>Fechar Turno</span>
              </button>
            )}
            
            {/* Botão Calendário */}
            {PROJECT_TYPES[project?.project_type]?.headerButtons?.includes('calendar') && (
              <button 
                className={`btn-view-mode ${viewMode === 'calendar' ? 'active' : ''}`}
                onClick={() => setViewMode('calendar')}
                title="Visualização Calendário"
              >
                <AppIcon name="calendar_month" />
                <span>Calendário</span>
              </button>
            )}
            
            {/* Botão Editar */}
            {PROJECT_TYPES[project?.project_type]?.headerButtons?.includes('edit') && (
              <button 
                className="btn-view-mode"
                onClick={() => setShowEditProjectModal(true)}
                title="Editar Projeto"
              >
                <AppIcon name="edit" />
                <span>Editar</span>
              </button>
            )}
          </div>

          {/* Separador */}
          <div className="header-separator"></div>

          {/* Botão Menu */}
          {!isCrmLikeProjectType(project?.project_type) && (
            <div className="project-menu-container">
              <button 
                className="btn-header"
                onClick={() => setProjectMenuOpen(!projectMenuOpen)}
              >
                <AppIcon name="more_vert" />
              </button>

              {projectMenuOpen && (
                <div className="project-menu-dropdown">
                  {project.project_type === 'bar_restaurant' && (
                    <>
                      <button
                        className="menu-item"
                        onClick={() => {
                          setShowServiceTaxModal(true);
                          setProjectMenuOpen(false);
                        }}
                      >
                        <AppIcon name="percent" />
                        <span>Configurar Taxa de Serviço</span>
                      </button>
                      <div className="menu-divider"></div>
                    </>
                  )}
                  <button
                    className="menu-item"
                    onClick={() => {
                      setShowEditProjectModal(true);
                      setProjectMenuOpen(false);
                    }}
                  >
                    <AppIcon name="settings" />
                    <span>Configurações do Projeto</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Conteúdo baseado no modo de visualização */}
      {/* ✅ Validar viewMode contra PROJECT_TYPES */}
      {!isValidViewMode(project?.project_type, viewMode) ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>
          <p>❌ Modo de visualização inválido: {viewMode}</p>
          <p>Tipo de projeto: {project?.project_type}</p>
        </div>
      ) : viewMode === 'kanban' ? (
        /* Board Kanban Padrão */
        <div className="kanban-board">
        {/* ✅ Validar se projeto permite listas */}
        {!PROJECT_TYPES[project?.project_type]?.hasLists ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>
            <p>❌ Este tipo de projeto não suporta listas</p>
          </div>
        ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {/* ✅ SortableContext para reordenar listas horizontalmente */}
          <SortableContext
            items={lists.map(l => l.id)}
            strategy={horizontalListSortingStrategy}
          >
            <div className="lists-container">
              {lists.map(list => (
                <SortableList key={list.id} list={list} isDragging={activeList?.id === list.id} isMinimized={minimizedLists[list.id]}>
                  {({ dragHandleProps }) => (<>
                  <div className="list-header" {...dragHandleProps}>
                  <div className="list-title-section">
                    <AppIcon name={list.icon || 'list'} className="list-icon" style={{ color: list.color }} />
                    {editingList === list.id ? (
                      <input
                        type="text"
                        defaultValue={list.title}
                        autoFocus
                        onBlur={(e) => {
                          // TODO: implementar edição inline
                          setEditingList(null);
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            setEditingList(null);
                          }
                        }}
                        className="list-title-input"
                      />
                    ) : (
                      <h3>{list.title}</h3>
                    )}
                    <span className="card-count">{list.cards.length}</span>
                  </div>
                  <div className="list-actions">
                    {/* ✅ NOVO: Botão de minimizar/expandir lista */}
                    <button
                      className="btn-list-action"
                      onClick={() => toggleListMinimized(list.id)}
                      title={minimizedLists[list.id] ? 'Expandir lista' : 'Minimizar lista'}
                    >
                      <AppIcon name={minimizedLists[list.id] ? 'unfold_more' : 'unfold_less'} />
                    </button>
                    {/* ✅ Botão de configurar lista direto */}
                    <button
                      className="btn-list-action"
                      onClick={() => {
                        setEditingListData(list);
                        setShowListConfigModal(true);
                      }}
                      title="Configurar lista"
                    >
                      <AppIcon name="settings" />
                    </button>
                  </div>
                </div>

                <SortableContext
                  items={list.cards.map(c => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <DroppableList list={list} onScroll={(e) => handleListScroll(list.id, e)}>
                    {/* ✅ Renderizar cards validando cardTypes permitidos */}
                    {list.cards.map(card => {
                      // Validar se tipo de card é permitido para este projeto
                      if (!isValidCardType(project?.project_type, card.entity_type)) {
                        console.warn(`⚠️ Card type '${card.entity_type}' não permitido para projeto '${project?.project_type}'`);
                        return null;
                      }

                      // Renderizar como SortableCard (funciona para todos os tipos)
                      return (
                        <SortableCard
                          key={card.id}
                          card={card}
                          listId={list.id}
                          onDelete={handleDeleteCard}
                          onOpenCard={handleOpenCard}
                          projectType={project?.project_type}
                        />
                      );
                    })}

                    {(listPagination[list.id]?.loading || list.cards_has_more) && (
                      <div style={{ padding: '0.5rem', textAlign: 'center' }}>
                        {listPagination[list.id]?.loading ? (
                          <small style={{ opacity: 0.8 }}>Carregando mais...</small>
                        ) : (
                          <button
                            type="button"
                            className="btn-add-card"
                            onClick={() => loadMoreCardsForList(list.id)}
                            style={{ width: '100%', justifyContent: 'center' }}
                          >
                            <span>Carregar mais</span>
                          </button>
                        )}
                      </div>
                    )}
                  </DroppableList>
                </SortableContext>

                  <button
                    className="btn-add-card"
                    onClick={() => handleAddCard(list.id)}
                  >
                    <AppIcon name="add" />
                    <span>Adicionar card</span>
                  </button>
                </>)}
                </SortableList>
              ))}

              {/* Botão Adicionar Lista */}
              <button className="btn-add-list" onClick={handleAddList}>
                <AppIcon name="add" />
                <span>Adicionar lista</span>
              </button>
            </div>
          </SortableContext>

          <DragOverlay>
            {activeCard ? (
              <div className="kanban-card dragging">
                <h4>{activeCard.title}</h4>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
        )}
      </div>
      ) : viewMode === 'tables' ? (
        /* Kanban de Mesas (Bar & Restaurante) */
        <BarRestaurantTableKanban projectId={projectId} onBack={() => setViewMode('tables')} />
      ) : viewMode === 'orders' ? (
        /* Kanban de Pedidos (Bar & Restaurante) */
        <BarRestaurantOrderKanban projectId={projectId} onBack={() => setViewMode('tables')} />
      ) : viewMode === 'calendar' ? (
        /* Visualização Calendário */
        <div className="calendar-view">
          {renderCalendar()}
        </div>
      ) : (
        /* Fallback: Modo desconhecido */
        <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>
          <p>❌ Modo de visualização desconhecido: {viewMode}</p>
        </div>
      )}

      {showNewManualClientModal && project?.project_type === 'gas_distributor' && (
        <GasDistributorNewManualClientModal
          projectId={projectId}
          onClose={() => setShowNewManualClientModal(false)}
          onCreated={async () => {
            setShowNewManualClientModal(false);
            await loadLists();
          }}
        />
      )}

      {/* Modal de Confirmação de Arquivamento */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(null)}>
          <div className="modal-delete" onClick={(e) => e.stopPropagation()}>
            <div className="modal-delete-header">
              <AppIcon name="warning" />
              <h3>Deletar Lista</h3>
            </div>
            <p>Você tem certeza que deseja deletar esta lista?</p>
            <p className="modal-warning">Todos os cards serão deletados. Esta ação não pode ser desfeita.</p>
            <div className="modal-delete-actions">
              <button
                className="btn-cancel-modal"
                onClick={() => setShowDeleteModal(null)}
              >
                Cancelar
              </button>
              <button
                className="btn-confirm-delete"
                onClick={() => handleDeleteList(showDeleteModal)}
              >
                <AppIcon name="delete" />
                Deletar Lista
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Evento (para eventos não-appointment) */}
      {showEventModal && selectedEvent && selectedEvent.event_type !== 'crm_appointment' && (
        <EventModal
          event={selectedEvent}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
            setSelectedDate(null);
          }}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          projects={[project]}
        />
      )}

      {/* Modal de Detalhes do Card (sempre por último para garantir que apareça) */}
      {selectedCard && (
        <>
          {/* Modal Gas Distributor para novo cliente (entity_type = gas_distributor_new_client) */}
          {project?.project_type === 'gas_distributor' && selectedCard?.entity_type === 'gas_distributor_new_client' ? (
            <GasDistributorNewClientCard
              card={selectedCard}
              onClose={() => {
                setSelectedCard(null);
                setSelectedCardListId(null);
              }}
            />
          ) : project?.project_type === 'gas_distributor' && selectedCard?.entity_type === 'gas_distributor_client' ? (
            /* Modal Gas Distributor para cliente cadastrado */
            <GasDistributorClientModal
              card={selectedCard}
              projectId={projectId}
              companyId={project?.company_id}
              onClose={() => {
                setSelectedCard(null);
                setSelectedCardListId(null);
              }}
            />
          ) : (
            /* Modal CRM para cards de contato */
            (isCrmLikeProjectType(project?.project_type) || !project) && selectedCard.entity_type === 'contact' ? (
              <CRMContactDetailModal
                card={selectedCard}
                listId={selectedCardListId}
                lists={lists}
                projectId={projectId}
                onClose={() => {
                  setSelectedCard(null);
                  setSelectedCardListId(null);
                }}
                onUpdate={handleUpdateCard}
                onDelete={handleDeleteCard}
                projectType={project?.project_type}
              />
            ) : (
              /* Modal padrão para cards normais */
              <CardDetailModal
                card={selectedCard}
                listId={selectedCardListId}
                lists={lists}
                onClose={() => {
                  setSelectedCard(null);
                  setSelectedCardListId(null);
                }}
                onUpdate={handleUpdateCard}
                onDelete={handleDeleteCard}
              />
            )
          )}
        </>
      )}

      {/* Modal de Configuração de Lista */}
      {showListConfigModal && (
        <>
          {/* ✅ Usar modal CRM específico para projetos CRM/CRM Multi e lista "Leads Recebidos" */}
          {(isCrmLikeProjectType(project?.project_type)) && editingListData?.title === 'Leads Recebidos' ? (
            <CRMListConfigModal
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleCRMListInfoUpdate}
              onDelete={handleDeleteList}
              projectId={projectId}
            />
          ) : (isCrmLikeProjectType(project?.project_type)) && editingListData?.title === 'Em Atendimento' ? (
            /* ✅ Modal específico para lista "Em Atendimento" */
            <CRMAtendimentoConfigModal
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleCRMListInfoUpdate}
              onDelete={handleDeleteList}
              projectId={projectId}
            />
          ) : project?.project_type === 'crm' && editingListData?.title === 'Aguardando Formulário' ? (
            /* ✅ Modal específico para lista "Aguardando Formulário" */
            <CRMAguardandoFormConfigModal
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleCRMListInfoUpdate}
              onDelete={handleDeleteList}
              projectId={projectId}
            />
          ) : project?.project_type === 'crm' && editingListData?.title?.startsWith('Follow-up') ? (
            /* ✅ Modal específico para listas "Follow-up 1/2/3" */
            <CRMFollowupListConfigModal
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleCRMListInfoUpdate}
              onDelete={handleDeleteList}
              projectId={projectId}
            />
          ) : project?.project_type === 'crm' && editingListData?.title === 'Equipe de Vendas' ? (
            /* ✅ Modal específico para lista "Equipe de Vendas" */
            <CRMEquipeVendasConfigModal
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleCRMListInfoUpdate}
              projectId={projectId}
            />
          ) : project?.project_type === 'marketing_campaign' && editingListData?.title === 'Equipe de Vendas' ? (
            /* ✅ Modal específico para lista "Equipe de Vendas" em Marketing Campaign */
            <MarketingCampaignEquipeVendasConfigModal
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleCRMListInfoUpdate}
              projectId={projectId}
            />
          ) : (isCrmLikeProjectType(project?.project_type)) ? (
            <CRMGenericListConfigModal
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleCRMListInfoUpdate}
              onDelete={handleDeleteList}
              projectId={projectId}
            />
          ) : project?.project_type === 'gas_distributor' && editingListData?.title === 'Entrega Imediata' ? (
            /* ✅ Modal específico para lista "Entrega Imediata" do Gas Distributor */
            <GasDistributorEntregaImediataConfigModal
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleCRMListInfoUpdate}
              projectId={projectId}
            />
          ) : project?.project_type === 'gas_distributor' && editingListData?.title === 'Entrega Agendada' ? (
            /* ✅ Modal específico para lista "Entrega Agendada" do Gas Distributor */
            <GasDistributorEntregaAgendadaConfigModal
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleCRMListInfoUpdate}
              projectId={projectId}
            />
          ) : project?.project_type === 'gas_distributor' && editingListData?.title === 'Em Entrega' ? (
            /* ✅ Modal específico para lista "Em Entrega" do Gas Distributor */
            <GasDistributorEmEntregaConfigModal
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleCRMListInfoUpdate}
              projectId={projectId}
            />
          ) : project?.project_type === 'gas_distributor' && editingListData?.title === 'Entregue' ? (
            /* ✅ Modal específico para lista "Entregue" do Gas Distributor */
            <GasDistributorEntregueConfigModal
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleCRMListInfoUpdate}
              projectId={projectId}
            />
          ) : project?.project_type === 'gas_distributor' && editingListData?.title === 'Distribuição Follow-up' ? (
            /* ✅ Modal específico para lista "Distribuição Follow-up" do Gas Distributor */
            <GasDistributorDistribuicaoFollowupConfigModal
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleCRMListInfoUpdate}
              projectId={projectId}
            />
          ) : project?.project_type === 'gas_distributor' && editingListData?.title === 'Clientes Novos' ? (
            /* ✅ Modal específico para lista "Clientes Novos" do Gas Distributor */
            <GasDistributorClientesNovosConfigModal
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleCRMListInfoUpdate}
              projectId={projectId}
            />
          ) : project?.project_type === 'gas_distributor' ? (
            /* ✅ Modal personalizado para listas extras do Gas Distributor */
            <GasDistributorExtraListConfigModal
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleCRMListInfoUpdate}
              onDelete={handleDeleteList}
              projectId={projectId}
            />
          ) : (
            <ListConfigModal
              isOpen={showListConfigModal}
              list={editingListData}
              onClose={() => {
                setShowListConfigModal(false);
                setEditingListData(null);
              }}
              onSave={handleSaveListConfig}
              onDelete={handleDeleteList}  // ✅ Passar função de deletar
            />
          )}
        </>
      )}

      {/* Modal de Evento do Calendário */}
      {showEventModal && (
        <EventModal
          event={selectedEvent}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
            setSelectedDate(null);
          }}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          projects={[project]}
          onOpenContact={async (contactId) => {
            try {
              const { data: contact, error } = await supabase
                .from('contatos')
                .select('*')
                .eq('id', contactId)
                .single();
              
              if (error) throw error;
              
              if (contact) {
                setSelectedCard({
                  ...contact,
                  entity_type: 'contact',
                  checklist: [],
                  comments: [],
                  attachments: [],
                  links: []
                });
                setSelectedCardListId(null);
              }
            } catch (err) {
              console.error('Erro ao buscar contato:', err);
              alert('Erro ao abrir contato: ' + err.message);
            }
          }}
        />
      )}

      {/* Modal de Timeline do Dia (CRM) */}
      {showDayTimeline && selectedDate && (
        <CRMTimelineModal
          date={selectedDate}
          events={calendarEvents.filter(e => {
            const eventDate = new Date(e.start_date);
            return eventDate.toDateString() === selectedDate.toDateString();
          })}
          onClose={() => {
            setShowDayTimeline(false);
            setSelectedDate(null);
          }}
          onEventClick={(event) => {
            setSelectedEvent(event);
            setShowDayTimeline(false);
            setShowEventModal(true);
          }}
          onNewEvent={() => {
            setShowDayTimeline(false);
            setSelectedEvent(null);
            setShowEventModal(true);
          }}
        />
      )}

      {/* Modal de Detalhes do Agendamento CRM */}
      {showEventModal && selectedEvent && selectedEvent.event_type === 'crm_appointment' && (
        <CRMAppointmentModal
          event={selectedEvent}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
            setSelectedDate(null);
          }}
          onOpenContact={async (contactId) => {
            try {
              // Primeiro fecha o modal de agendamento
              setShowEventModal(false);
              setSelectedEvent(null);
              setSelectedDate(null);
              
              // Pequeno delay para garantir que o estado seja atualizado
              await new Promise(resolve => setTimeout(resolve, 100));
              
              const { data: contact, error } = await supabase
                .from('contatos')
                .select('*')
                .eq('id', contactId)
                .single();
              
              if (error) throw error;
              
              if (contact) {
                setSelectedCard({
                  ...contact,
                  id: contact.id,
                  entity_id: contact.id,
                  entity_type: 'contact',
                  checklist: [],
                  comments: [],
                  attachments: [],
                  links: []
                });
                setSelectedCardListId(null);
              }
            } catch (err) {
              console.error('Erro ao buscar contato:', err);
              alert('Erro ao abrir contato: ' + err.message);
            }
          }}
        />
      )}

      {/* Modal de Evento (para eventos não-appointment) */}
      {showEventModal && selectedEvent && selectedEvent.event_type !== 'crm_appointment' && (
        <EventModal
          event={selectedEvent}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
            setSelectedDate(null);
          }}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          projects={[project]}
        />
      )}

      {/* Modal de Escolha de Tipo de Card */}
      <AddCardTypeModal
        isOpen={showAddCardTypeModal}
        onClose={() => {
          setShowAddCardTypeModal(false);
          setSelectedListForCard(null);
        }}
        onSelectType={handleSelectCardType}
        projectType={project?.project_type}
      />

      {/* Modal de Seleção de Contato */}
      <SelectContactModal
        isOpen={showSelectContactModal}
        onClose={() => {
          setShowSelectContactModal(false);
          setSelectedListForCard(null);
        }}
        onSelectContact={handleSelectContact}
        projectId={projectId}
      />

      {/* Modal de Busca de Contatos (CRM) */}
      <SearchContactModal
        isOpen={showSearchContactModal}
        onClose={() => setShowSearchContactModal(false)}
        projectId={projectId}
        onSelectContact={(contact, listId) => {
          setSelectedCard(contact);
          setSelectedCardListId(listId);
          setShowSearchContactModal(false);
        }}
      />

      {/* Modal de Edição do Projeto */}
      {project?.project_type === 'gas_distributor' ? (
        <EditGasDistributorProjectModal
          isOpen={showEditProjectModal}
          onClose={() => setShowEditProjectModal(false)}
          project={project}
          onSave={() => {
            loadProject();
            setShowEditProjectModal(false);
          }}
        />
      ) : project?.project_type === 'crm_multi' ? (
        <EditCRMMultiProjectModal
          isOpen={showEditProjectModal}
          onClose={() => setShowEditProjectModal(false)}
          project={project}
          onSave={() => {
            loadProject();
            setShowEditProjectModal(false);
          }}
        />
      ) : project?.project_type === 'marketing_campaign' ? (
        <EditMarketingCampaignProjectModal
          isOpen={showEditProjectModal}
          onClose={() => setShowEditProjectModal(false)}
          project={project}
          onSave={() => {
            loadProject();
            setShowEditProjectModal(false);
          }}
        />
      ) : project?.project_type === 'crm' ? (
        <EditCRMProjectModalNew
          isOpen={showEditProjectModal}
          onClose={() => setShowEditProjectModal(false)}
          project={project}
          onSave={() => {
            loadProject();
            setShowEditProjectModal(false);
          }}
        />
      ) : (
        <EditCRMProjectModal
          isOpen={showEditProjectModal}
          onClose={() => setShowEditProjectModal(false)}
          project={project}
          onSave={() => {
            loadProject();
            setShowEditProjectModal(false);
          }}
        />
      )}

      {/* Modal de Detalhe de Pedido Bar & Restaurante */}
      {showBarRestaurantOrderModal && selectedBarRestaurantOrder && (
        <BarRestaurantOrderDetailModal
          card={selectedBarRestaurantOrder}
          onClose={() => {
            setShowBarRestaurantOrderModal(false);
            setSelectedBarRestaurantOrder(null);
            loadLists();
          }}
          onStatusChange={(newStatus) => {
            // Lógica para alterar status manualmente será implementada aqui
            console.log('Status alterado para:', newStatus);
            loadLists();
          }}
        />
      )}

      {/* Modal de Detalhe de Mesa Bar & Restaurante */}
      {showBarRestaurantTableModal && selectedBarRestaurantTable && (
        <BarRestaurantTableDetailModal
          table={selectedBarRestaurantTable}
          onClose={() => {
            setShowBarRestaurantTableModal(false);
            setSelectedBarRestaurantTable(null);
            loadBarRestaurantTables();
          }}
        />
      )}

      {showCloseShiftModal && (
        <div className="modal-overlay" onClick={() => setShowCloseShiftModal(false)}>
          <div className="modal-delete" onClick={(e) => e.stopPropagation()}>
            <div className="modal-delete-header">
              <AppIcon name="warning" />
              <h3>Fechar Turno</h3>
            </div>
            <p>Tem certeza? Isso fechará todas as mesas abertas e arquivará os pedidos em andamento.</p>
            <div className="modal-delete-actions">
              <button
                className="btn-cancel-modal"
                onClick={() => setShowCloseShiftModal(false)}
                disabled={closingShift}
              >
                Cancelar
              </button>
              <button
                className="btn-confirm-delete"
                onClick={async () => {
                  try {
                    setClosingShift(true);
                    const response = await fetch('/api/bar-restaurant/turnos/close', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ projectId })
                    });

                    const result = await response.json();
                    if (!response.ok || !result.success) {
                      throw new Error(result.error || 'Erro ao fechar turno');
                    }

                    window.dispatchEvent(new Event('bar-restaurant-turno-updated'));
                    loadBarRestaurantTables();
                    setViewMode('tables');
                    setShowCloseShiftModal(false);
                  } catch (error) {
                    console.error('[KanbanBoard] Erro ao fechar turno:', error);
                    alert('Erro ao fechar turno. Tente novamente.');
                  } finally {
                    setClosingShift(false);
                  }
                }}
                disabled={closingShift}
              >
                {closingShift ? 'Fechando...' : 'Fechar Turno'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Taxa de Serviço */}
      {showServiceTaxModal && (
        <ServiceTaxModal
          projectId={projectId}
          onClose={() => setShowServiceTaxModal(false)}
          onSave={() => {
            // Recarregar dados se necessário
            loadProject();
          }}
        />
      )}
    </div>
  );
}
